<script setup>
/* Passwortfeld mit Auge: verdeckt als Standard, der Knopf rechts im Feld schaltet um.
 *
 * Alle Attribute (autocomplete, required, disabled, id ...) gehen unverändert an das <input>,
 * Enter im umgebenden <form> sendet wie bisher. Nach dem Absenden des Formulars ist das Feld
 * wieder verdeckt; beim Schließen eines Dialogs wird die Komponente ohnehin entfernt.
 * Das Aussehen des <input> bestimmt der Aufrufer (die Stile dort per :deep), hier nur der Platz
 * für den Knopf.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';

defineOptions({ inheritAttrs: false });
const model = defineModel({ type: String, default: '' });

const shown = ref(false);
const input = ref(null);
let form = null;
const hide = () => { shown.value = false; };

onMounted(() => {
  form = input.value?.form ?? null;
  form?.addEventListener('submit', hide);
});
onBeforeUnmount(() => form?.removeEventListener('submit', hide));
</script>

<template>
  <span class="pw-wrap">
    <input ref="input" v-model="model" class="pw-input" v-bind="$attrs" :type="shown ? 'text' : 'password'" />
    <button
      type="button"
      class="pw-eye"
      :aria-label="$t(shown ? 'auth.hidePassword' : 'auth.showPassword')"
      :aria-pressed="shown"
      @click="shown = !shown"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path v-if="shown" fill="currentColor" d="M11.83 9 15 12.16V12a3 3 0 0 0-3-3h-.17m-4.3.8 1.55 1.55c-.05.21-.08.42-.08.65a3 3 0 0 0 3 3c.22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53a5 5 0 0 1-5-5c0-.79.2-1.53.53-2.2M2 4.27l2.28 2.28.45.45C3.08 8.3 1.78 10 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.43.42L19.73 22 21 20.73 3.27 3M12 7a5 5 0 0 1 5 5c0 .64-.13 1.26-.36 1.82l2.93 2.93c1.5-1.25 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-4 .7l2.17 2.15C10.74 7.13 11.35 7 12 7z" />
        <path v-else fill="currentColor" d="M12 9a3 3 0 0 1 3 3 3 3 0 0 1-3 3 3 3 0 0 1-3-3 3 3 0 0 1 3-3m0-4.5c5 0 9.27 3.11 11 7.5-1.73 4.39-6 7.5-11 7.5S2.73 16.39 1 12c1.73-4.39 6-7.5 11-7.5M3.18 12a9.821 9.821 0 0 0 17.64 0 9.821 9.821 0 0 0-17.64 0z" />
      </svg>
    </button>
  </span>
</template>

<style scoped>
.pw-wrap {
  position: relative;
  display: block;
}
.pw-wrap input.pw-input {
  padding-right: 42px;
}
.pw-eye {
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  background: none;
  border: 0;
  border-radius: 7px;
  color: var(--muted);
  cursor: pointer;
}
.pw-eye:hover {
  color: var(--green);
}
.pw-eye:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: -2px;
}
</style>
